"use client";

import { useSyncExternalStore } from "react";

/**
 * Persisted underwriter "working set" (GIQS-162 follow-up): the underwriter
 * filter a team/company admin applies on the Market Finder grid is treated as
 * their current book of work, not a throwaway filter — it also gates the
 * sidebar Updated badge and survives navigation/reloads via localStorage.
 * The backend re-validates: ids outside the caller's authorization scope are
 * dropped server-side, so this can only narrow, never widen.
 */

const STORAGE_KEY = "mf-underwriter-working-set";

let workingSet: string[] = loadInitial();
const listeners = new Set<() => void>();

function loadInitial(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === "string") : [];
  } catch {
    return [];
  }
}

export function getUnderwriterWorkingSet(): string[] {
  return workingSet;
}

export function setUnderwriterWorkingSet(ids: string[]) {
  const next = [...new Set(ids)].sort();
  if (next.join(",") === workingSet.join(",")) return;
  workingSet = next;
  try {
    if (next.length > 0) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Storage unavailable (private mode) — working set is session-only.
  }
  listeners.forEach((listener) => listener());
}

export function subscribeUnderwriterWorkingSet(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const EMPTY: string[] = [];

export function useUnderwriterWorkingSet(): string[] {
  return useSyncExternalStore(
    subscribeUnderwriterWorkingSet,
    getUnderwriterWorkingSet,
    () => EMPTY,
  );
}
